'use client';

import React from 'react';
import styled from 'styled-components';
import { Input } from '@/components/forms/controls/Input/Input';
import { DatePicker } from '@/components/forms/controls/DatePicker/DatePicker';
import { TimePicker } from '@/components/forms/controls/TimePicker/TimePicker';
import { TextArea } from '@/components/forms/controls/TextArea/TextArea';
import { Button } from '@/components/ui-components/Button/Button';
import { i18n } from '@/config/i18n';

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Open Sans', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

export interface MaintenanceWindowFormData {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  comments: string;
}

export interface MaintenanceWindowFormErrors {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  submit?: string;
}

export interface MaintenanceWindowFormProps {
  formData: MaintenanceWindowFormData;
  errors: MaintenanceWindowFormErrors;
  loading?: boolean;
  onFormDataChange: (data: Partial<MaintenanceWindowFormData>) => void;
  onErrorsChange: (errors: Partial<MaintenanceWindowFormErrors>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitButtonText?: string;
  disableTimingFields?: boolean;
}

export const MaintenanceWindowForm: React.FC<MaintenanceWindowFormProps> = ({
  formData,
  errors,
  loading = false,
  onFormDataChange,
  onErrorsChange,
  onSubmit,
  onCancel,
  submitButtonText,
  disableTimingFields = false,
}) => {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disableTimingFields) return;
    onFormDataChange({ startDate: e.target.value });
    onErrorsChange({ startDate: '' });
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disableTimingFields) return;
    onFormDataChange({ startTime: e.target.value });
    onErrorsChange({ startTime: '' });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disableTimingFields) return;
    onFormDataChange({ endDate: e.target.value });
    onErrorsChange({ endDate: '', endTime: '' });
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disableTimingFields) return;
    onFormDataChange({ endTime: e.target.value });
    onErrorsChange({ endTime: '' });
  };

  const handleCommentsChange = (value: string) => {
    onFormDataChange({ comments: value });
  };

  // Calculate minimum time for start time (current time if today, otherwise no restriction)
  const getStartTimeMin = (): string | undefined => {
    if (disableTimingFields) return undefined;
    const today = new Date().toISOString().split('T')[0];
    if (formData.startDate === today) {
      return `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
    }
    return undefined;
  };

  // Calculate minimum time for end time
  const getEndTimeMin = (): string | undefined => {
    if (disableTimingFields) return undefined;
    const today = new Date().toISOString().split('T')[0];
    
    // If same day as start, use start time as minimum
    if (formData.startDate === formData.endDate && formData.startTime) {
      return formData.startTime;
    }
    
    // If end date is today, use current time as minimum
    if (formData.endDate === today) {
      return `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
    }
    
    return undefined;
  };

  return (
    <FormSection>
      <FormRow>
        <div>
          <DatePicker
            label={`${i18n.monitors.detail.maintenanceWindow.labels.startDate}*`}
            value={formData.startDate}
            onChange={handleStartDateChange}
            min={new Date().toISOString().split('T')[0]}
            disabled={disableTimingFields}
          />
          {errors.startDate && <ErrorText>{errors.startDate}</ErrorText>}
        </div>

        <div>
          <TimePicker
            label={`${i18n.monitors.detail.maintenanceWindow.labels.startTime}*`}
            value={formData.startTime}
            onChange={handleStartTimeChange}
            disabled={disableTimingFields}
            min={getStartTimeMin()}
          />
          {errors.startTime && <ErrorText>{errors.startTime}</ErrorText>}
        </div>
      </FormRow>

      <FormRow>
        <div>
          <DatePicker
            label={`${i18n.monitors.detail.maintenanceWindow.labels.endDate}*`}
            value={formData.endDate}
            onChange={handleEndDateChange}
            min={formData.startDate || new Date().toISOString().split('T')[0]}
            disabled={disableTimingFields}
          />
          {errors.endDate && <ErrorText>{errors.endDate}</ErrorText>}
        </div>

        <div>
          <TimePicker
            label={`${i18n.monitors.detail.maintenanceWindow.labels.endTime}*`}
            value={formData.endTime}
            onChange={handleEndTimeChange}
            disabled={disableTimingFields}
            min={getEndTimeMin()}
          />
          {errors.endTime && <ErrorText>{errors.endTime}</ErrorText>}
        </div>
      </FormRow>

      <TextArea
        label={i18n.monitors.detail.maintenanceWindow.labels.comments}
        value={formData.comments}
        onChange={handleCommentsChange}
        placeholder={i18n.monitors.detail.maintenanceWindow.placeholders.comments}
        rows={4}
      />

      {errors.submit && <ErrorText>{errors.submit}</ErrorText>}

      <ButtonGroup>
        <Button variant="white" onClick={onCancel} disabled={loading}>
          {i18n.monitors.detail.maintenanceWindow.buttons.cancel}
        </Button>
        <Button variant="primary" onClick={onSubmit} loading={loading} disabled={loading}>
          {submitButtonText || i18n.monitors.detail.maintenanceWindow.buttons.schedule}
        </Button>
      </ButtonGroup>
    </FormSection>
  );
};

export default MaintenanceWindowForm;

